import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getOnboardingSteps,
  getRoleLabel,
  isOnboardingDismissedLocally,
  markOnboardingDismissedLocally,
  shouldShowOnboarding,
  withProfileLink,
} from '../../utils/onboarding';
import './WelcomeOnboarding.css';

const WelcomeOnboarding: React.FC = () => {
  const { authState, completeOnboarding } = useAuth();
  const user = authState.user;
  const dialogRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (user?.id) {
      setDismissed(isOnboardingDismissedLocally(user.id));
      setStepIndex(0);
    } else {
      setDismissed(false);
    }
  }, [user?.id]);

  const steps = useMemo(() => {
    if (!user) return [];
    return withProfileLink(getOnboardingSteps(user.role), user.id);
  }, [user]);

  const visible =
    !authState.loading && !!user && !dismissed && shouldShowOnboarding(user) && steps.length > 0;

  const finish = useCallback(async () => {
    if (!user?.id || saving) return;
    setSaving(true);
    markOnboardingDismissedLocally(user.id);
    setDismissed(true);
    await completeOnboarding();
    setSaving(false);
  }, [user?.id, saving, completeOnboarding]);

  useEffect(() => {
    if (!visible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        void finish();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    dialogRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [visible, finish]);

  if (!visible || !user) {
    return null;
  }

  const safeIndex = Math.min(Math.max(stepIndex, 0), steps.length - 1);
  const step = steps[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === steps.length - 1;

  const goPrev = () => {
    setStepIndex(i => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (isLast) {
      void finish();
      return;
    }
    setStepIndex(i => Math.min(steps.length - 1, i + 1));
  };

  return (
    <div className="welcome-onboarding" role="presentation">
      <div className="welcome-onboarding__backdrop" aria-hidden />

      <div
        ref={dialogRef}
        className="welcome-onboarding__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-onboarding-title"
        aria-describedby="welcome-onboarding-lead welcome-onboarding-step-desc"
        tabIndex={-1}
      >
        <header className="welcome-onboarding__header">
          <div>
            <p className="welcome-onboarding__eyebrow">Getting started</p>
            <h2 id="welcome-onboarding-title" className="welcome-onboarding__title">
              Welcome, {user.username || 'artist'}
            </h2>
            <p id="welcome-onboarding-lead" className="welcome-onboarding__lead">
              As a <strong>{getRoleLabel(user.role)}</strong>, here&apos;s a quick tour of DHH Collab.
              Step through at your pace — or skip anytime.
            </p>
          </div>
          <button
            type="button"
            className="welcome-onboarding__skip"
            onClick={() => void finish()}
            disabled={saving}
            aria-label="Skip onboarding"
          >
            Skip
          </button>
        </header>

        <div className="welcome-onboarding__progress-row" aria-live="polite">
          <p className="welcome-onboarding__progress">
            Step {safeIndex + 1} of {steps.length}
          </p>
          <div className="welcome-onboarding__dots" aria-hidden>
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`welcome-onboarding__dot ${i === safeIndex ? 'welcome-onboarding__dot--active' : ''} ${i < safeIndex ? 'welcome-onboarding__dot--done' : ''}`}
              />
            ))}
          </div>
        </div>

        <div className="welcome-onboarding__step" key={step.id}>
          <span className="welcome-onboarding__step-num" aria-hidden>
            {safeIndex + 1}
          </span>
          <div className="welcome-onboarding__step-body">
            <h3 className="welcome-onboarding__item-title">{step.title}</h3>
            <p id="welcome-onboarding-step-desc" className="welcome-onboarding__item-desc">
              {step.description}
            </p>
            {step.to && step.ctaLabel && (
              <Link to={step.to} className="welcome-onboarding__link" onClick={() => void finish()}>
                {step.ctaLabel}
                <span aria-hidden> →</span>
              </Link>
            )}
          </div>
        </div>

        <footer className="welcome-onboarding__footer">
          <button
            type="button"
            className="welcome-onboarding__nav welcome-onboarding__nav--prev"
            onClick={goPrev}
            disabled={isFirst || saving}
          >
            Previous
          </button>
          <button
            type="button"
            className="welcome-onboarding__nav welcome-onboarding__nav--next"
            onClick={goNext}
            disabled={saving}
          >
            {saving ? 'Saving…' : isLast ? 'Got it — start exploring' : 'Next'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default WelcomeOnboarding;
